import { CurrentSettingsContext } from 'global/cnr/contexts/SettingsDataContext';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Icon, List, Segment } from 'semantic-ui-react';
import { PapsContext } from '../../cnr/contexts/PapsContext';
import { getUpdateConfirmation } from '../../cnr/reducers/reducerHelpers/dispatchProps';
import { uniqueKey } from '../../common/keys';
import ConfirmationQuestion from '../../components/alerts/ConfirmationQuestion';
import UiSaveButtons from '../../components/buttons/UiSaveButtons';
import { createRefPath } from '../../firestoreData/appData/appRefPaths';
import { deleteStorageFolder } from '../../functions/fbBucketFunctions';
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper';
import { _settingsFs } from '../actions/getSettings';
import { ParentContext } from 'global/cnr/contexts/ParentContext';

const subCollections = [
  '_conversations',
  '_gallery_direct',
  '_globals',
  '_googleSheetsData',
  '_notes',
  '_notifications',
  '_zones',
  'staticViews',
  'files',
]

const deleteTypes = {
  base: 'base',
  subs: 'subs',
  views: 'views',
  storage: 'storage',
  settings: 'settings'
}

/**
 * 
 * @returns a `DeleteEvent` component
 * @description Presents the data paths that will be removed from the database. The `deleteEventAllPromise` function will remove the data. 
 */
const DeleteEvent = (props) => {

  const preText = 'Are you sure you want to '

  const parentContext = useContext(ParentContext);
  const { fn_cls } = parentContext ?? {}

  // papsContext
  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext ?? {}
  const { pathViews, storageRootPath, settingsDocName, rootPath } = paps_state ?? {}

  // currentSettingsContext
  const currentSettingsContext = useContext(CurrentSettingsContext);
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});

  let viewItemKeys = aps_viewItems ? Object.keys(aps_viewItems) : []
  viewItemKeys = viewItemKeys ? viewItemKeys.sort() : []

  const [showConfirmation, setShowConfirmation] = useState();
  const [showListConfirmation, setShowListConfirmation] = useState();
  const [showDataConfirmation, setShowDataConfirmation] = useState();
  const [updating, setUpdating] = useState()
  const [updatingType, setUpdatingType] = useState()
  const [updates, setUpdates] = useState({})
  const [confirmation, setConfirmation] = useState()

  const [allItems, setAllItems] = useState({ baseItems: null, subItems: null, viewItems: null, storageItems: null, settingsItems: null });

  useEffect(() => {

    const baseItems = []
    const subItems = []
    const viewItems = []
    const storageItems = []
    const settingsItems = []

    Object.keys(pathViews).forEach(key => (baseItems.push(listItem(key, pathViews[key]))))
    subCollections.forEach(sc => (subItems.push(listItem(sc, '.../' + sc))))
    viewItemKeys.forEach(key => (viewItems.push(listItem(key, '.../' + key))))
    storageItems.push(listItem(deleteTypes.storage, storageRootPath))
    settingsItems.push(listItem(deleteTypes.settings, settingsDocName))

    setAllItems({
      baseItems,
      subItems,
      viewItems,
      storageItems,
      settingsItems
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdates = (r) => {
    setUpdatingType()
    setUpdates(r)
  }

  const handleListConfirmDelete = (opts) => {
    if (showListConfirmation === opts.type) {
      setShowListConfirmation()
    } else {
      setShowListConfirmation(opts.type)
    }
  }
  const handleCancelListConfirmation = () => setShowListConfirmation()
  const handleShowConfirmation_delete = () => setShowConfirmation(true)
  const handleShowConfirmation_deleteData = () => setShowDataConfirmation(true)
  const handleShowConfirmation_cancel = () => setShowConfirmation(false)

  const handleDeleteEvent = async () => {
    setShowConfirmation()
    setUpdating(true)
    // fsfn_deleteFullEventFromDatabase
    const fnResult = await fn_cls.deleteTheFullEventFromDatabase(pathViews, viewItemKeys, storageRootPath)
    setConfirmation(getUpdateConfirmation(fnResult))
  }

  const handleDeleteEventData = async () => {
    setShowConfirmation()
    setUpdating(true)
    const fnResult = await fn_cls.deleteFullEventFromDatabase(pathViews, viewItemKeys, storageRootPath, true)
    setConfirmation(getUpdateConfirmation(fnResult))
  }

  const handleDeleteEventCollections = () => {
    setUpdatingType(showListConfirmation)
    switch (showListConfirmation) {
      case deleteTypes.storage:
        deleteStorageFolder(storageRootPath)
        // fn_cls.deleteEventCollections(pathViews, viewItemKeys, handleUpdates)
        break;
      case deleteTypes.subs:
        fn_cls.deleteEventCollections(pathViews, subCollections, handleUpdates)
        break;
      case deleteTypes.views:
        fn_cls.deleteEventCollections(pathViews, viewItemKeys, handleUpdates)
        break;
      case deleteTypes.settings:
        const settingsRef = createRefPath([_settingsFs.root, pathViews.events])
        database_fns.delete_doc(settingsRef, handleUpdates)
        break;
      default:
        break;
    }
  }

  const listConfirmationQuestion = (type) => <ConfirmationQuestion
    header={'Please Confirm?'}
    question={<div style={{ whiteSpace: 'pre-line' }}>{preText + 'delete this event? This action cannot be undone. \n\nRoot Path: ' + rootPath + '. \n\nSettings Path: ' + settingsDocName}</div>}
    buttonCaption={'Delete'}
    buttonColor={'red'}
    handleNo={handleCancelListConfirmation}
    handleYes={handleDeleteEventCollections}
    noModal={true}
    noFullHeight={true}
  />

  const confirmationQuestion = () => <ConfirmationQuestion
    header={'Please Confirm?'}
    question={<div style={{ whiteSpace: 'pre-line' }}>{preText + 'delete this event? This action cannot be undone. \n\nRoot Path: ' + rootPath + '. \n\nSettings Path: ' + settingsDocName}</div>}
    buttonCaption={'Delete'}
    buttonColor={'red'}
    handleNo={handleShowConfirmation_cancel}
    handleYes={handleDeleteEvent}
    noModal={true}
  />

  const confirmationDataQuestion = () => <ConfirmationQuestion
    header={'Please Confirm?'}
    question={preText + 'delete data for this event? This action cannot be undone.'}
    buttonCaption={'Delete'}
    buttonColor={'red'}
    handleNo={handleShowConfirmation_cancel}
    handleYes={handleDeleteEventData}
    noModal={true}
  />

  const listIcon = (key, item) => {
    const { icon, color, loading } = item
    return <Icon key={uniqueKey('de', 'lii', key)} name={icon} color={color} loading={loading} />
  }

  const listItem = (key, content) => <List.Item key={uniqueKey('de', 'li', key)}>
    {updates && updates[key] && <List.Content floated='right' key={uniqueKey('de', 'li', 1, key)}>
      {listIcon(key, updates[key])}
    </List.Content>}
    <List.Content key={uniqueKey('de', 'li', 2, key)}>
      <List.Header >{_.startCase(key)}</List.Header>
      <List.Description >{content}</List.Description>
    </List.Content>
  </List.Item>

  const list = (type, items) => <Segment inverted key={uniqueKey('de', 's', type)}>
    <List relaxed divided inverted key={uniqueKey('de', 'l', type)}>
      {items}
    </List>
  </Segment>

  const groupFooter = (type) => <UiSaveButtons save={{ oc: handleListConfirmDelete, caption: 'Delete ' + _.startCase(type), icon: 'arrow right', color: 'blue', opts: { type } }} />

  const listWrapper = (type, items, noDelete) => {
    if (showListConfirmation === type) {
      return listConfirmationQuestion(type)
    } else {
      return <Wrapper
        header={_.startCase(type)}
        content={list(type, items)}
        footer={!noDelete && groupFooter(type)}
        wrapperType={wrapperTypes.paddedFooter}
        updatingCaption={'Delete ' + type}
        updating={updatingType && updatingType === type}
        asSegment={true}
        inverted={true}
        css={{ container: 'h0' }}
      />
    }
  }

  const content = () => {

    const { baseItems, subItems, viewItems, storageItems, settingsItems } = allItems ?? {}

    return <React.Fragment>
      {listWrapper('Path', baseItems, true)}
      {listWrapper(deleteTypes.subs, subItems)}
      {listWrapper(deleteTypes.views, viewItems)}
      {listWrapper(deleteTypes.storage, storageItems)}
      {listWrapper(deleteTypes.settings, settingsItems)}
    </React.Fragment >
  }

  const footer = () => <UiSaveButtons
    save={{ oc: handleShowConfirmation_delete, caption: 'Delete The Full Event', icon: 'arrow right', color: 'blue' }}
    preview={{ oc: handleShowConfirmation_deleteData, caption: 'Delete Event Data', icon: 'arrow right', color: 'blue' }}
  />

  const wrapper = () => <Wrapper
    content={allItems && content()}
    footer={footer()}
    wrapperType={wrapperTypes.paddedFooter}
    updatingCaption={'Deleting Event'}
    updating={updating}
    confirmation={confirmation}
  />

  if (showConfirmation) {
    return confirmationQuestion()
  } else if (showDataConfirmation) {
    return confirmationDataQuestion()
  } else {
    return wrapper()
  }
}

export default DeleteEvent